import { getCurrentProject, useProjectsStore } from '@modules/projects'
import type { Project } from '@modules/projects/projects.type'
import { updateWorkspaceContext } from '@modules/user'
import { setLastUsedWorkspaceIdOnCurrentUser } from '@modules/user/utils/setLastUsedWorkspaceIdOnCurrentUser'
import { getCurrentWorkspace, getWorkspacesByUserId, useWorkspacesStore } from '@modules/workspaces'
import type { Workspace } from '@modules/workspaces/workspaces.type'

export type FinalizeOnboardingShellStateParams = {
  preferredWorkspaceId?: string | null
  maxAttempts?: number
  delayMs?: number
}

export type FinalizeOnboardingShellStateResult = {
  workspace: Workspace
  project: Project
}

async function resolvePreferredWorkspace(preferredWorkspaceId: string | null | undefined): Promise<Workspace | null> {
  await getWorkspacesByUserId({ force: true })

  const { workspaces } = useWorkspacesStore.getState()
  if (preferredWorkspaceId) {
    const preferred = workspaces.find((item) => item.workspaceId === preferredWorkspaceId) ?? null
    if (preferred) {
      useWorkspacesStore.getState().setCurrentWorkspace(preferred)
      setLastUsedWorkspaceIdOnCurrentUser(preferred.workspaceId)
      await updateWorkspaceContext(preferred.workspaceId)
      return preferred
    }
  }

  return getCurrentWorkspace()
}

export async function finalizeOnboardingShellState(
  params: FinalizeOnboardingShellStateParams = {}
): Promise<FinalizeOnboardingShellStateResult> {
  const { preferredWorkspaceId, maxAttempts = 5, delayMs = 1000 } = params

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const workspace = await resolvePreferredWorkspace(preferredWorkspaceId)

    if (workspace) {
      const project = await getCurrentProject(workspace.workspaceId)
      const resolvedProject = project ?? useProjectsStore.getState().currentProject

      if (resolvedProject) {
        return { workspace, project: resolvedProject }
      }
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }

  throw new Error('Workspace and project not available after onboarding')
}
